import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import axios from "../../../utills/api";
import { toast } from "react-toastify";
import TableContainer from "../../../Components/Common/TableContainer";
import { Link } from "react-router-dom";
import HousekeepingCategoryModal from "./CategoryModal/HousekeepingCategoryModal";

const API_BASE = "housekeeping";
const CATEGORY_API = "housekeeping-services/categories";

const Housekeeping = () => {
  document.title = "HMS | Housekeeping";
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]); // 🔑 fetched categories
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [categoryModal, setCategoryModal] =
    useState(false);
  const [newService, setNewService] = useState({
    category: "",
    name: "",
    publishedOn: "",
    status: "Active",
  });

  const toggleModal = () => setModalOpen(!modalOpen);

  // ✅ Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await axios.get(CATEGORY_API);
      const data = Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];
      setCategories(data);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      toast.error("Failed to fetch categories");
    }
  };

  // ✅ Fetch services
  const fetchServices = async () => {
    try {
      const res = await axios.get(API_BASE);
      const data = Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];
      setServices(data);
    } catch (err) {
      console.error("Failed to fetch services:", err);
      toast.error("Failed to fetch services");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchServices();
  }, []);

  const handleInputChange = (e) => {
    setNewService({ ...newService, [e.target.name]: e.target.value });
  };

  const handleAddOrUpdateService = async () => {
    if (!newService.name || !newService.category) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      let res, savedService;

      if (editingService) {
        res = await axios.put(`${API_BASE}/${editingService._id}`, newService);
      } else {
        res = await axios.post(API_BASE, newService);
      }

      savedService = res.data?.data || res.data;

      if (!savedService || !savedService._id) throw new Error("Invalid API response");

      // Update state
      setServices((prev) =>
        editingService
          ? prev.map((s) => (s._id === editingService._id ? savedService : s))
          : [...prev, savedService]
      );

      toast.success(editingService ? "Service updated successfully" : "Service added successfully");

      // Reset form
      setNewService({ category: "", name: "", publishedOn: "", status: "Active" });
      setEditingService(null);

      // ✅ Close modal
      toggleModal();
    } catch (err) {
      console.error("Failed to save service:", err.response || err.message);
      toast.error("Failed to save service");
    }
  };


  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/${id}`);
      setServices(services.filter((s) => s._id !== id));
      toast.success("Service deleted successfully");
    } catch (err) {
      toast.error("Failed to delete service");
    }
  };

  const filteredServices = services.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.category.toLowerCase().includes(search.toLowerCase())
  );

  const serviceColumns = useMemo(
    () => [
      { header: "Service ID", accessorKey: "_id", cell: ({ getValue }) => getValue()?.slice(0, 5) },
      { header: "Category", accessorKey: "category" },
      { header: "Name", accessorKey: "name" },
      {
        header: "Published On",
        accessorKey: "publishedOn",
        cell: ({ cell }) =>
          cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : "",
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ cell }) => (
          <span
            className={`badge ${cell.getValue() === "Active"
                ? "text-uppercase bg-success-subtle text-success"
                : "text-uppercase bg-danger-subtle text-danger"
              }`}
          >
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="d-flex justify-content-center align-items-center gap-2">
            <Link
              onClick={() => {
                setEditingService(row.original);
                setNewService(row.original);
                toggleModal();
              }}
              className="btn btn-sm btn-soft-primary"
              style={{ width: "28px", height: "28px" }}
            >
              <i className="ri-edit-line"></i>
            </Link>

            {/* onClick={() => handleDelete(row.original._id)}
              className="btn btn-sm btn-soft-danger"
              style={{ width: "28px", height: "28px" }} */}

            <button
              className="btn btn-sm btn-soft-danger"
              onClick={() => {
                setSelectedBooking(row.original);
                setDeleteModal(true);
              }}
            >
              <i className="las la-trash-alt"></i>
            </button>
          </div>
        ),
      },
    ],
    [services]
  );

  return (
    <>
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h5 className="fs-16 mb-0">Housekeeping Services</h5>

          <div className="d-flex" style={{ gap: "10px" }}>
            <Row>
              <Col md={12}>
                <Input
                  type="text"
                  placeholder="Search services..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
            </Row>

            <Button
              color="success"
              onClick={() => setCategoryModal(true)}
            >
              + Housekeeping Category
            </Button>

            <Button color="primary" onClick={toggleModal}>
              + Add Service
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <TableContainer
              columns={serviceColumns}
              data={filteredServices}
              customPageSize={5}
              isPagination
              className="text-nowrap"
            />
          </div>
        </CardBody>
      </Card>

      {/* Modal for Add/Edit */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {editingService ? "Edit Service" : "Add Service"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Category</Label>
              <Input
                type="select"
                name="category"
                value={newService.category}
                onChange={handleInputChange}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Service Name</Label>
              <Input
                type="text"
                name="name"
                value={newService.name}
                onChange={handleInputChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Published On</Label>
              <Input
                type="date"
                name="publishedOn"
                value={newService.publishedOn}
                onChange={handleInputChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input
                type="select"
                name="status"
                value={newService.status}
                onChange={handleInputChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </Input>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddOrUpdateService}>
            {editingService ? "Update Service" : "Add Service"}
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <HousekeepingCategoryModal
        isOpen={categoryModal}
        toggle={() =>
          setCategoryModal(false)
        }
      />
    </>

  );
};

export default Housekeeping;
